import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { DownloadSimple, Link as LinkIcon, QrCode } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * QrGenerator — Genera QR cliente-side (qrcode) para imprimir en cartelería.
 *
 * Props:
 *  - defaultUrl: URL inicial (ej. URL del marketplace del taller).
 *  - title: título del bloque.
 *  - brandColor: color del módulo del QR (opcional, default #0033A0).
 *  - subtitle: texto bajo el QR (ej. "Escanea para reservar").
 *  - allowEdit: si false, el campo URL es solo lectura.
 *  - hiResSize: tamaño del PNG hi-res en px (default 2048).
 *  - filenameBase: prefijo del archivo descargado.
 */
export default function QrGenerator({
  defaultUrl = "",
  title = "Generador de QR",
  brandColor = "#0033A0",
  subtitle = "",
  allowEdit = true,
  hiResSize = 2048,
  filenameBase = "autoglow-qr",
}) {
  const [url, setUrl] = useState(defaultUrl);
  const canvasRef = useRef(null);
  const [svgString, setSvgString] = useState("");

  useEffect(() => { setUrl(defaultUrl); }, [defaultUrl]);

  useEffect(() => {
    if (!url) return;
    const canvas = canvasRef.current;
    if (canvas) {
      QRCode.toCanvas(canvas, url, {
        width: 320,
        margin: 2,
        errorCorrectionLevel: "H",
        color: { dark: brandColor, light: "#ffffff" },
      }).catch(() => {});
    }
    QRCode.toString(url, {
      type: "svg",
      margin: 2,
      errorCorrectionLevel: "H",
      color: { dark: brandColor, light: "#ffffff" },
    }).then(setSvgString).catch(() => setSvgString(""));
  }, [url, brandColor]);

  const downloadPng = async () => {
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        width: hiResSize,
        margin: 2,
        errorCorrectionLevel: "H",
        color: { dark: brandColor, light: "#ffffff" },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${filenameBase}-${hiResSize}px.png`;
      a.click();
      toast.success(`PNG descargado (${hiResSize}×${hiResSize}px)`);
    } catch (e) {
      toast.error("No se pudo generar el PNG");
    }
  };

  const downloadSvg = () => {
    if (!svgString) return toast.error("SVG no disponible");
    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `${filenameBase}.svg`;
    a.click();
    URL.revokeObjectURL(u);
    toast.success("SVG vectorial descargado");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success("URL copiada al portapapeles");
  };

  return (
    <div className="ag-card p-6" data-testid="qr-generator">
      <div className="flex items-center gap-2 mb-1">
        <QrCode size={18} weight="duotone" className="text-[var(--ag-gold)]" />
        <h3 className="font-display text-xl font-bold tracking-tighter">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-[var(--ag-text-dim)] mb-4">{subtitle}</p>}

      <div className="grid md:grid-cols-2 gap-6 items-center">
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">URL destino</Label>
            <div className="flex gap-2 mt-1">
              <Input value={url}
                onChange={(e)=>setUrl(e.target.value)}
                readOnly={!allowEdit}
                className="rounded-sm font-mono text-xs"
                data-testid="qr-url-input"
              />
              <Button size="icon" variant="outline" onClick={copyLink} title="Copiar URL"
                className="rounded-sm shrink-0 border-[var(--ag-border)]" data-testid="qr-copy-btn">
                <LinkIcon size={14} />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Button onClick={downloadPng} className="ag-btn-gold w-full rounded-sm whitespace-normal h-auto py-2 text-xs sm:text-sm" data-testid="qr-download-png">
              <DownloadSimple size={14} className="mr-2 shrink-0" weight="bold" />
              <span className="text-left">Descargar PNG · {hiResSize}px (alta resolución)</span>
            </Button>
            <Button onClick={downloadSvg} variant="outline"
              className="w-full rounded-sm border-[var(--ag-border)] text-[var(--ag-text)] hover:bg-[var(--ag-midnight-soft)] whitespace-normal h-auto py-2 text-xs sm:text-sm"
              data-testid="qr-download-svg">
              <DownloadSimple size={14} className="mr-2 shrink-0" />
              <span className="text-left">Descargar SVG vectorial</span>
            </Button>
          </div>
          <p className="text-[10px] text-[var(--ag-text-dim)] leading-relaxed">
            <b className="text-[var(--ag-text)]">PNG hi-res</b> ideal para impresión digital (tarjetas, pegatinas, flyers).<br/>
            <b className="text-[var(--ag-text)]">SVG</b> escalable sin pérdida — perfecto para cartelería física grande.
          </p>
        </div>

        <div className="flex flex-col items-center gap-2 py-4">
          <div className="bg-white p-4 rounded-md inline-block shadow-lg">
            <canvas
              ref={canvasRef}
              data-testid="qr-canvas"
              className="block max-w-full h-auto"
              style={{ width: "min(280px, 70vw)", height: "auto", aspectRatio: "1 / 1" }}
            />
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Previsualización</div>
        </div>
      </div>
    </div>
  );
}
